import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Avatar, Box, Container, CssBaseline, LinearProgress, Stack, Typography } from '@mui/material'
import { styled, ThemeProvider } from '@mui/material/styles'

import AppLogo from '@/common/components/AppLogo/AppLogo'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'

// Match the TechnicalBackground styling from TbxLoadingPage
const TechnicalBackground = styled(Box)(({ theme }) => ({
    background: theme.palette.background.default,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
}))

// Match the TechnicalContainer styling from TbxLoadingPage
const TechnicalContainer = styled(Container)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: '8px',
    padding: theme.spacing(4),

    '&::after': {
        content: '"DRAWING NO: TT-INIT-001 | SCALE: 1:1 | REV: A"',
        position: 'absolute',
        bottom: -12,
        right: 24,
        fontSize: '12px',
        color: theme.palette.text.secondary,
        background: theme.palette.background.paper,
        padding: '2px 8px',
        border: `1px solid ${theme.palette.action.disabled}`,
        borderRadius: '4px',
    },
}))

// Match the TechnicalProgressContainer styling from TbxLoadingPage
const TechnicalProgressContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.active}`,
    padding: theme.spacing(3),
    borderRadius: '8px',
}))

// Match the TechnicalTypography styling from TbxLoadingPage
const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

const TechnicalSkeleton = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
}))

const TbxInitializingApp = () => {
    const { t } = useTranslation()
    const { showLibellulaWhitelabelling, showStrikerWhitelabelling } = useToolBoxTreatments()

    // Optimize theme selection with memoization
    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <TechnicalBackground>
                <TechnicalContainer maxWidth="md">
                    <Stack spacing={4}>
                        <AppLogo />
                        {/* Technical Progress Section - matching TbxLoadingPage structure */}
                        <TechnicalProgressContainer>
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="center"
                                mb={2}
                            >
                                <TechnicalTypography
                                    color={theme.palette.text.secondary}
                                    variant="strong2"
                                >
                                    {t('SYSTEM INITIALISATION')}
                                </TechnicalTypography>
                            </Stack>

                            {/* Current Step Information - matching TbxLoadingPage structure */}
                            <Stack
                                alignItems="center"
                                direction="row"
                                mt={3}
                                spacing={2}
                            >
                                <Avatar>
                                    <HugeiconsIcon
                                        color={theme.palette.secondary.main}
                                        icon={Loading03Icon}
                                        size={18}
                                    />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack
                                        alignItems="center"
                                        direction="row"
                                        justifyContent="space-between"
                                    >
                                        <TechnicalTypography
                                            color={theme.palette.text.secondary}
                                            sx={{ fontWeight: 600 }}
                                            variant="small"
                                        >
                                            {t('INITIALIZING APPLICATION WORKSPACE').toUpperCase()}
                                        </TechnicalTypography>
                                        <TechnicalTypography
                                            color={theme.palette.text.secondary}
                                            variant="caption"
                                        >
                                            INIT-SYS-001
                                        </TechnicalTypography>
                                    </Stack>
                                    <Stack mt={1}>
                                        <TechnicalTypography
                                            color={theme.palette.text.secondary}
                                            variant="caption"
                                        >
                                            {t('Please wait while we prepare your workspace...')}
                                        </TechnicalTypography>
                                    </Stack>
                                </Box>
                            </Stack>
                        </TechnicalProgressContainer>
                    </Stack>
                </TechnicalContainer>
            </TechnicalBackground>
        </ThemeProvider>
    )
}

// Export memoized component since it has no dynamic props
export default memo(TbxInitializingApp)
